import { State, Action, StateContext, Selector, createSelector } from '@ngxs/store';
import { EngineCameraStateModel } from './engine.camera.state.model';
import { 
  StoreCamera,
  ClearCamera,
  MoveCamera,
  StopCamera,
  ZoomCamera,
  PanCameraX,
  RotateCameraX,
  PanCameraY,
  RotateCameraY,
  SetPanMode, 
  SetZoomed,
  LockCamera} from './engine.camera.actions';
import { CameraService } from '../../camera.service';
import { CameraMotionModel } from '../../models/camera-motion.model';

const engineCameraStateDefaults = {
  storedCameras: {},
  cameraMotion: new CameraMotionModel(),
  panMode: 'rotate',
  zoomed: false,
  lockCamera: false
};


@State<EngineCameraStateModel>({
  name: 'engineCamera',
  defaults: engineCameraStateDefaults
})
export class EngineCameraState {
  /**
   * Selectors
   */
  static getStoredCamera(id: string) {
    return createSelector([EngineCameraState], (state: EngineCameraStateModel) => {
      return state.storedCameras[id] || null;
    });
  }

  @Selector()
  static getCameraMotion(state: EngineCameraStateModel) {
    return state.cameraMotion;
  }

  @Selector()
  static getPanMode(state: EngineCameraStateModel) {
    return state.panMode;
  }

  @Selector()
  static getZoomed(state: EngineCameraStateModel) {
    return state.zoomed;
  }

  @Selector()
  static getLockCamera(state: EngineCameraStateModel) {
    return state.lockCamera;
  }

  /**
   * Actions
   */
  @Action(StoreCamera)
  storeCamera(context: StateContext<EngineCameraStateModel>, action: StoreCamera) {
    const camera = action.camera || this.cameraService.getCamera();
    const storedCameras = context.getState().storedCameras;
    
    storedCameras[action.id] = {...camera};

    context.patchState({
      storedCameras: {...storedCameras}
    });
  }

  @Action(ClearCamera)
  clearCamera(context: StateContext<EngineCameraStateModel>, action: ClearCamera) {
    const storedCameras = context.getState().storedCameras;
    delete storedCameras[action.id];

    context.patchState({
      storedCameras: {...storedCameras}
    });
  }

  @Action(MoveCamera)
  moveCamera(context: StateContext<EngineCameraStateModel>, action: MoveCamera) {
    context.patchState({
      cameraMotion: {...action.cameraMotion}
    });
  }

  @Action(StopCamera)
  stopCamera(context: StateContext<EngineCameraStateModel>, action: StopCamera) {
    context.patchState({
      cameraMotion: new CameraMotionModel()
    });
  }

  @Action(ZoomCamera)
  zoomCamera(context: StateContext<EngineCameraStateModel>, action: ZoomCamera) {
    const { cameraMotion } = context.getState();
    cameraMotion.zoom = action.value;

    context.patchState({
      cameraMotion: {...cameraMotion }
    });
  }

  @Action(PanCameraX)
  panCameraX(context: StateContext<EngineCameraStateModel>, action: PanCameraX) {
    const { cameraMotion } = context.getState();
    cameraMotion.panX = action.value;

    context.patchState({
      cameraMotion: {...cameraMotion }
    });
  }

  @Action(PanCameraY)
  panCameraY(context: StateContext<EngineCameraStateModel>, action: PanCameraY) {
    const { cameraMotion } = context.getState();
    cameraMotion.panY = action.value;

    context.patchState({
      cameraMotion: {...cameraMotion }
    });
  }

  @Action(RotateCameraX)
  rotateCameraX(context: StateContext<EngineCameraStateModel>, action: RotateCameraX) {
    const { cameraMotion } = context.getState();
    cameraMotion.rotateX = action.value;

    context.patchState({
      cameraMotion: {...cameraMotion }
    });
  }

  @Action(RotateCameraY)
  rotateCameraY(context: StateContext<EngineCameraStateModel>, action: RotateCameraY) {
    const { cameraMotion } = context.getState();
    cameraMotion.rotateY = action.value;

    context.patchState({
      cameraMotion: {...cameraMotion }
    });
  }
  
  @Action(SetPanMode)
  setPanMode(context: StateContext<EngineCameraStateModel>, action: SetPanMode) {
    context.patchState({
      panMode: action.mode
    });
  }

  @Action(SetZoomed)
  setZoomed(context: StateContext<EngineCameraStateModel>, action: SetZoomed) {
    context.patchState({
      zoomed: action.zoomed
    });
  }
  
  @Action(LockCamera)
  lockCamera(context: StateContext<EngineCameraStateModel>, action: LockCamera) {
    context.patchState({
      lockCamera: action.lock
    });
  }


  constructor(
    private cameraService: CameraService
  ) { }
}
